import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import SliderInput from '../../components/SliderInput';

const HoursCalculator = () => {
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:30');
  const [breakMin, setBreakMin] = useState(60);
  const [hourlyRate, setHourlyRate] = useState(500);

  const result = useMemo(() => {
    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    let startMins = sh * 60 + sm;
    let endMins = eh * 60 + em;
    if (endMins < startMins) endMins += 24 * 60; // next day
    const grossMin = endMins - startMins;
    const netMin = Math.max(grossMin - breakMin, 0);
    return {
      grossHours: grossMin / 60,
      netHours: netMin / 60,
      grossMin,
      netMin,
      earnings: (netMin / 60) * hourlyRate,
    };
  }, [startTime, endTime, breakMin, hourlyRate]);

  return (
    <CalculatorLayout
      title="Hours Calculator"
      subtitle="Track work hours, breaks, and calculate earnings at your hourly rate."
      category="Other"
      description={<p>Enter your start and end times, subtract break time, and see total hours worked. Optionally enter your hourly rate in ₹ to calculate your earnings for the session.</p>}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm mb-1.5 block">Start Time</Label>
              <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">End Time</Label>
              <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
            </div>
          </div>
          <SliderInput label="Break (minutes)" value={breakMin} onChange={setBreakMin} min={0} max={240} step={5} />
          <SliderInput label="Hourly Rate" unit="₹" unitColor="saffron" value={hourlyRate} onChange={setHourlyRate} min={0} max={10000} step={50} />
        </div>

        <div className="bg-gradient-to-br from-saffron-50 to-indgreen-50/40 rounded-xl border border-slate-200 p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Hours Worked</div>
          <div className="font-display text-4xl font-bold text-bharat-navy display-font mt-1">
            {Math.floor(result.netMin / 60)}h {Math.round(result.netMin % 60)}m
          </div>
          <div className="text-xs text-slate-500 mt-1">{result.netHours.toFixed(4)} decimal hours</div>

          <div className="grid grid-cols-2 gap-3 mt-5 text-sm">
            <Box label="Gross Hours" value={result.grossHours.toFixed(2)} />
            <Box label="Break" value={`${breakMin} min`} />
            <div className="col-span-2">
              <Box label="Earnings" value={'₹' + Math.round(result.earnings).toLocaleString('en-IN')} tone="green" big />
            </div>
          </div>
        </div>
      </div>
    </CalculatorLayout>
  );
};

const Box = ({ label, value, tone, big }) => {
  const c = tone === 'green' ? 'text-indgreen-700' : 'text-bharat-navy';
  return (
    <div className="bg-white rounded-md p-2.5 border border-slate-100">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`display-font font-semibold mt-0.5 ${c} ${big ? 'text-xl' : ''}`}>{value}</div>
    </div>
  );
};

export default HoursCalculator;
